<template>
    <VJoyWrapper no-margin class="modify-availability" align="stretch" direction="column" wrap="nowrap">
        <div>
            <VJoyRadioGroup direction="horizontal" name="availability">
                <VJoyRadio
                    v-model="selectAvailabilityModel"
                    data-testid="navbar-availability-set-available-no"
                    :value="AvailabilityUpdateRequestResourceValueEnum.NotAvailable"
                >
                    <span class="modify-availability__selectable-item-content">
                        <!-- <VJoyIcon name="ban" /> -->
                        {{ t('profile.availability.edit.NOT_AVAILABLE') }}
                    </span>
                </VJoyRadio>
                <VJoyRadio
                    v-model="selectAvailabilityModel"
                    data-testid="navbar-availability-set-available-yes"
                    :value="AvailabilityUpdateRequestResourceValueEnum.Available"
                >
                    <span class="modify-availability__selectable-item-content">
                        <!-- <VJoyIcon name="check" /> -->
                        {{ t('profile.availability.edit.AVAILABLE') }}
                    </span>
                </VJoyRadio>
            </VJoyRadioGroup>
        </div>

        <div v-if="isNotAvailable">
            <VJoyLabel class="modify-availability__label">{{ t('profile.availability.modify.next.availability.title') }}</VJoyLabel>
            <VJoyRadioGroup :direction="radiosDirection">
                <VJoyRadio
                    v-for="item in months"
                    :key="item"
                    v-model="nextAvailabilityDateByMonthSelected"
                    data-testid="navbar-set-availability-by-month"
                    :value="item"
                >
                    {{ t('profile.availability.edit.next.availability.date.choice', [item]) }}
                </VJoyRadio>
            </VJoyRadioGroup>

            <VJoyWrapper item-stretch>
                <MaltDatePicker
                    data-testid="navbar-date-picker"
                    class="modify-availability__calendar"
                    :class="{'modify-availability__calendar--relative': showCalendarRelative}"
                    :model-value="state?.nextAvailabilityDate"
                    :min-date="minDateForAvailabilityInput"
                    :max-date="maxDateForAvailabilityInput"
                    name="nextAvailabilityDate"
                    @update:model-value="nextAvailabilityDateUpdate"
                    @click.stop
                />
            </VJoyWrapper>
        </div>
        <div v-else>
            <VJoyLabel class="modify-availability__label">{{ t('profile.availability.modify.frequency.title') }}</VJoyLabel>
            <VJoyWrapper item-stretch>
                <VJoySelect v-model="frequencyModel" data-testid="navbar-set-availability-frequency">
                    <option v-for="frequency in frequencies" :key="frequency" :value="frequency">
                        {{ t(`profile.frequency.text.${frequency}`) }}
                    </option>
                </VJoySelect>
            </VJoyWrapper>
        </div>
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {computed, ref, watch, onMounted} from 'vue';
    import type {PropType} from 'vue';
    import type {AvailabilityUpdateRequestResource, AvailabilityUpdateRequestResourceFrequencyEnum} from '@navbar-api';
    import {AvailabilityResourceFrequencyEnum, AvailabilityUpdateRequestResourceValueEnum} from '@navbar-api';
    import {addMonths, addYears, format} from 'date-fns';
    import {MaltDatePicker} from '#components';
    import {useTranslation} from '#imports';
    import {VJoyIcon, VJoyRadio, VJoyRadioGroup, VJoyLabel, VJoyWrapper, VJoySelect} from '@maltjoy/core-vue';

    const {t} = useTranslation();
    const props = defineProps({
        state: {type: Object as PropType<AvailabilityUpdateRequestResource | null>, default: null},
        radiosDirection: {type: String as PropType<'horizontal' | 'vertical'>, required: true},
        showCalendarRelative: {type: Boolean, default: false},
    });

    const selectAvailabilityModel = ref<AvailabilityUpdateRequestResourceValueEnum>();

    const frequencyModel = ref<AvailabilityUpdateRequestResourceFrequencyEnum>();
    const frequencies = Object.values(AvailabilityResourceFrequencyEnum);
    const months = [1, 3, 6];

    const emit = defineEmits<{stateUpdated: [state: Partial<AvailabilityUpdateRequestResource>]}>();

    onMounted(() => {
        selectAvailabilityModel.value = props.state?.value;
        frequencyModel.value = props.state?.frequency;
        nextAvailabilityDateByMonthSelected.value = numberOfMonthSelected.value;
    });

    watch(
        () => props.state?.nextAvailabilityDate,
        () => {
            if (numberOfMonthSelected.value) {
                nextAvailabilityDateByMonthSelected.value = numberOfMonthSelected.value;
            }
        },
    );

    watch(selectAvailabilityModel, () => {
        if (selectAvailabilityModel.value) {
            emit('stateUpdated', {value: selectAvailabilityModel.value});
        }
    });

    watch(frequencyModel, () => {
        if (frequencyModel.value) {
            emit('stateUpdated', {frequency: frequencyModel.value});
        }
    });

    function nextAvailabilityDateUpdate(value?: Date) {
        console.log('nextAvailabilityDateUpdate', value);
        emit('stateUpdated', {nextAvailabilityDate: value});
    }

    const isNotAvailable = computed(() => selectAvailabilityModel.value === AvailabilityUpdateRequestResourceValueEnum.NotAvailable);

    const nextAvailabilityDateByMonthSelected = ref<number>();

    watch(nextAvailabilityDateByMonthSelected, () => {
        const month = Number(nextAvailabilityDateByMonthSelected.value);
        const nextAvailabilityDate = addMonths(new Date(), month);

        emit('stateUpdated', {nextAvailabilityDate});
    });

    const numberOfMonthSelected = computed(() => {
        if (!props.state?.nextAvailabilityDate) {
            return undefined;
        }

        for (const numberOfMonth of months) {
            const now = addMonths(new Date(), numberOfMonth);

            if (getDateToString(now) === getDateToString(props.state.nextAvailabilityDate)) {
                return numberOfMonth;
            }
        }

        return undefined;
    });

    const minDateForAvailabilityInput = new Date();
    const maxDateForAvailabilityInput = addYears(new Date(), 3);

    function getDateToString(date: Date) {
        return format(date, 'yyyy-MM-dd');
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .modify-availability {
        &__selectable-item-content {
            display: flex;
            gap: var(--joy-core-spacing-2);
            align-items: center;
        }

        &__label {
            margin-bottom: var(--joy-core-spacing-6);

            label {
                @include text.getFontBySize(primary-450);
            }
        }

        .joy-radio-label {
            @include text.getFontBySize(primary-400);
        }

        &__calendar {
            &--relative .joy-calendar__calendar {
                position: relative !important;
                transform: none !important;
            }
        }
    }
</style>
